import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import AdminSettingsTab from './admin/AdminSettingsTab';
import AdminOverviewTab from './admin/AdminOverviewTab';
import AdminAnalyticsTab from './admin/AdminAnalyticsTab';
import AdminBookingsTab from './admin/AdminBookingsTab';
import AdminLabelsTab from './admin/AdminLabelsTab';
import AdminExpensesTab from './admin/AdminExpensesTab';
import AdminCustomersTab from './admin/AdminCustomersTab';
import AdminRefundsTab from './admin/AdminRefundsTab';
import AdminOrphanedPaymentsTab from './admin/AdminOrphanedPaymentsTab';
import { logError } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboardFull = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [adminToken, setAdminToken] = useState('');
  
  // New states for full dashboard
  const [statusUpdate, setStatusUpdate] = useState({ bookingId: null, packageId: null, status: '' });
  const [cancellationReason, setCancellationReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [newExpense, setNewExpense] = useState({ date: '', type: '', amount: '', description: '', mileage: '' });
  
  // Phase 1 Enhancement States
  const [revenueAnalytics, setRevenueAnalytics] = useState(null);
  const [customerAnalytics, setCustomerAnalytics] = useState(null);
  const [settings, setSettings] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [usernameForm, setUsernameForm] = useState({ newUsername: '', confirmPassword: '' });
  const [settingsMessage, setSettingsMessage] = useState('');

  // Define callback functions BEFORE useEffect hooks to avoid temporal dead zone issues
  const loadDashboardData = useCallback(async (token) => {
    try {
      // Load all bookings
      const bookingsRes = await fetch(`${API_URL}/api/admin/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (bookingsRes.ok) {
        const bookingsData = await bookingsRes.json();
        setBookings(bookingsData);
      }

      // Load customers
      const customersRes = await fetch(`${API_URL}/api/admin/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (customersRes.ok) {
        const customersData = await customersRes.json();
        setCustomers(customersData);
      }

      // Load expenses
      const expensesRes = await fetch(`${API_URL}/api/admin/expenses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (expensesRes.ok) {
        const expensesData = await expensesRes.json();
        setExpenses(expensesData);
      }
      
      // Load enhanced analytics
      try {
        const revenueRes = await fetch(`${API_URL}/api/admin/analytics/revenue?period=month`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (revenueRes.ok) {
          const revenueData = await revenueRes.json();
          setRevenueAnalytics(revenueData);
        }
        
        const customerRes = await fetch(`${API_URL}/api/admin/analytics/customers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (customerRes.ok) {
          const customerData = await customerRes.json();
          setCustomerAnalytics(customerData);
        }
      } catch (error) {
        logError('Failed to load enhanced analytics:', error);
      }

    } catch (error) {
      logError('Failed to load dashboard data:', error);
    }
  }, []);
  
  const loadSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (error) {
      logError('Failed to load settings:', error);
    }
  }, [adminToken]);

  useEffect(() => {
    const token = sessionStorage.getItem('adminToken'); // TODO: Security - migrate to httpOnly cookies (see SECURITY_TODO.md)
    if (!token) {
      navigate('/admin/login');
      return;
    }
    setAdminToken(token);
    
    // Set loading to false immediately to show dashboard
    setIsLoading(false);
    
    // Load data in background
    loadDashboardData(token);
  }, [navigate, loadDashboardData]);

  // Auto-load settings when Settings tab is accessed
  useEffect(() => {
    if (activeTab === 'settings' && adminToken && !settings) {
      loadSettings();
    }
  }, [activeTab, adminToken, settings, loadSettings]);
  
  const updateSettings = async (updatedSettings) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(updatedSettings)
      });
      
      if (res.ok) {
        setSettingsMessage('Settings updated successfully!');
        setTimeout(() => setSettingsMessage(''), 3000);
        loadSettings();
      } else {
        setSettingsMessage('Failed to update settings');
      }
    } catch (error) {
      setSettingsMessage('Failed to update settings');
    }
  };
  
  const changePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setSettingsMessage('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      setSettingsMessage('Password must be at least 8 characters');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSettingsMessage('Password changed successfully!');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setSettingsMessage(data.detail || 'Failed to change password');
      }
    } catch (error) {
      setSettingsMessage('Failed to change password');
    }
  };

  const changeUsername = async () => {
    if (!usernameForm.newUsername || usernameForm.newUsername.length < 3) {
      setSettingsMessage('Username must be at least 3 characters');
      return;
    }
    
    if (!usernameForm.confirmPassword) {
      setSettingsMessage('Please enter your password to confirm');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/change-username`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          new_username: usernameForm.newUsername,
          current_password: usernameForm.confirmPassword
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSettingsMessage('Username changed successfully! Please login with your new username.');
        setUsernameForm({ newUsername: '', confirmPassword: '' });
        setTimeout(() => {
          handleLogout();
        }, 2000);
      } else {
        setSettingsMessage(data.detail || 'Failed to change username');
      }
    } catch (error) {
      setSettingsMessage('Failed to change username');
    }
  };

  const updatePackageStatus = async (bookingId, packageId, newStatus) => {
    try {
      let cancellation_reason = null;
      if (newStatus === 'cancelled') {
        cancellation_reason = cancellationReason === 'other' ? customReason : cancellationReason;
      }
      const payload = {
        status: newStatus,
        cancellation_reason,
      };

      const response = await fetch(
        `${API_URL}/api/admin/bookings/${bookingId}/packages/${packageId}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminToken}`
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        alert(`Package status updated to: ${newStatus}`);
        await loadDashboardData(adminToken);
        setStatusUpdate({ bookingId: null, packageId: null, status: '' });
        setCancellationReason('');
        setCustomReason('');
      } else {
        const errorData = await response.json();
        alert(`Failed to update status: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      logError('Status update error:', error);
      alert('Failed to update status');
    }
  };

  const refundBooking = async (booking) => {
    if (!booking?.id) return;
    const confirmMsg = `Refund booking #${booking.confirmation_number}?\n\n` +
      `Customer: ${booking.customer_name || booking.user_name || booking.user_email || ''}\n` +
      `Amount: $${Number(booking.total_cost || 0).toFixed(2)}\n\n` +
      `This will issue a FULL refund through Stripe and email the customer. ` +
      `This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    const cancel = window.confirm('Also CANCEL the pickup (mark booking as cancelled)?\n\nClick OK to cancel the pickup, Cancel to keep it active.');
    const note = window.prompt('Optional note for the customer (visible in their refund email). Leave blank to skip:', '') || '';
    try {
      const res = await fetch(`${API_URL}/api/admin/bookings/${booking.id}/refund`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ cancel_pickup: cancel, note }),
      });
      const data = await res.json();
      if (res.ok) {
        alert(`✅ Refund issued. Stripe refund ID: ${data.refund_id}`);
        await loadDashboardData(adminToken);
      } else {
        alert(`Refund failed: ${data.detail || 'unknown error'}`);
      }
    } catch (err) {
      logError('Refund error', err);
      alert('Refund failed (network/server error)');
    }
  };

  const addExpense = async () => {
    if (!newExpense.date || !newExpense.type || !newExpense.amount) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/expenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(newExpense)
      });

      if (response.ok) {
        alert('Expense added successfully');
        setNewExpense({ date: '', type: '', amount: '', description: '', mileage: '' });
        loadDashboardData(adminToken);
      }
    } catch (error) {

      alert('Failed to add expense');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  // Calculate revenue metrics
  const totalRevenue = bookings.reduce((sum, b) => sum + (b.total_cost || 0), 0);
  const stripeFees = totalRevenue * 0.029 + (bookings.length * 0.30); // Stripe fees
  const totalExpenses = expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
  const netProfit = totalRevenue - stripeFees - totalExpenses;

  // Pre-compute the "Labels" tab dataset once per bookings change — avoids
  // re-running filter().sort() on every keystroke / re-render elsewhere.
  const bookingsWithLabels = useMemo(
    () =>
      bookings
        .filter((b) => b.packages?.some((pkg) => pkg.label_url))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [bookings],
  );

  const cancellationReasons = [
    'Customer not available',
    'Package too large',
    'Incorrect address',
    'Missing label',
    'Weather delay',
    'Vehicle issue',
    'Safety concern',
    'other'
  ];

  if (isLoading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading admin dashboard...</div>;
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <div style={{ 
        backgroundColor: 'white', 
        borderBottom: '1px solid #e5e7eb',
        padding: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.5rem'
      }}>
        <h1 style={{ fontSize: '1.25rem', fontWeight: '700' }}>ReturnPal Admin</h1>
        <Button onClick={handleLogout} variant="outline" style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}>
          <LogOut size={14} style={{ marginRight: '0.5rem' }} />
          Logout
        </Button>
      </div>

      {/* Tab Navigation */}
      <div style={{ 
        backgroundColor: 'white', 
        borderBottom: '2px solid #e5e7eb',
        padding: '0 1rem',
        display: 'flex',
        gap: '0.5rem',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}>
        {['overview', 'bookings', 'labels', 'customers', 'expenses', 'refunds', 'orphans', 'analytics', 'support', 'settings'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            data-testid={`tab-${tab}`}
            style={{
              padding: '1rem 0.75rem',
              borderBottom: activeTab === tab ? '3px solid #10b981' : '3px solid transparent',
              fontWeight: activeTab === tab ? '600' : '400',
              color: activeTab === tab ? '#10b981' : '#6b7280',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textTransform: 'capitalize',
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            {tab === 'orphans' ? 'Orphan Payments' : tab}
          </button>
        ))}
      </div>

      <div style={{ padding: '1rem' }}>
        {/* OVERVIEW TAB - extracted to admin/AdminOverviewTab.jsx */}
        {activeTab === 'overview' && (
          <AdminOverviewTab
            totalRevenue={totalRevenue}
            netProfit={netProfit}
            totalExpenses={totalExpenses}
            bookings={bookings}
            revenueAnalytics={revenueAnalytics}
            customerAnalytics={customerAnalytics}
          />
        )}

        {/* BOOKINGS TAB - extracted to admin/AdminBookingsTab.jsx */}
        {activeTab === 'bookings' && (
          <AdminBookingsTab
            bookings={bookings}
            statusUpdate={statusUpdate}
            setStatusUpdate={setStatusUpdate}
            cancellationReason={cancellationReason}
            setCancellationReason={setCancellationReason}
            customReason={customReason}
            setCustomReason={setCustomReason}
            updatePackageStatus={updatePackageStatus}
            refundBooking={refundBooking}
          />
        )}

        {/* LABELS & QR CODES TAB - extracted to admin/AdminLabelsTab.jsx */}
        {activeTab === 'labels' && (
          <AdminLabelsTab
            bookings={bookings}
            bookingsWithLabels={bookingsWithLabels}
            setActiveTab={setActiveTab}
          />
        )}

        {/* CUSTOMERS TAB */}
        {/* CUSTOMERS TAB - extracted to admin/AdminCustomersTab.jsx */}
        {activeTab === 'customers' && (
          <AdminCustomersTab customers={customers} />
        )}

        {/* EXPENSES TAB - extracted to admin/AdminExpensesTab.jsx */}
        {activeTab === 'expenses' && (
          <AdminExpensesTab
            expenses={expenses}
            totalExpenses={totalExpenses}
            newExpense={newExpense}
            setNewExpense={setNewExpense}
            addExpense={addExpense}
          />
        )}
        {/* REFUNDS TAB */}
        {activeTab === 'refunds' && (
          <AdminRefundsTab adminToken={adminToken} />
        )}

        {/* ORPHANED PAYMENTS TAB */}
        {activeTab === 'orphans' && (
          <AdminOrphanedPaymentsTab adminToken={adminToken} />
        )}

        {/* SUPPORT TAB */}
        {activeTab === 'support' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700' }}>Customer Support</h2>
            </div>

            <Card style={{ padding: '2rem', textAlign: 'center' }}>
              <MessageSquare size={64} color="#10b981" style={{ margin: '0 auto 1rem' }} />
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Support System Coming Soon</h3>
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Customer inquiry management and email responses will be available here.
              </p>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '1rem' }}>
                For now, customers can reach you at: <strong>support@getreturnpal.com</strong>
              </p>
            </Card>
          </div>
        )}

        {/* ANALYTICS TAB - Phase 2 */}
        {activeTab === 'analytics' && (
          <AdminAnalyticsTab adminToken={adminToken} />
        )}


        {/* SETTINGS TAB - extracted to admin/AdminSettingsTab.jsx */}
        {activeTab === 'settings' && (
          <AdminSettingsTab
            settings={settings}
            setSettings={setSettings}
            updateSettings={updateSettings}
            settingsMessage={settingsMessage}
            usernameForm={usernameForm}
            setUsernameForm={setUsernameForm}
            changeUsername={changeUsername}
            passwordForm={passwordForm}
            setPasswordForm={setPasswordForm}
            changePassword={changePassword}
          />
        )}

      </div>
    </div>
  );
};

export default AdminDashboardFull;
